import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthApi from 'utils/api/Auth';
import BLApi from 'utils/api/BL';
import CarApi from 'utils/api/Cars';
import CompanyApi from 'utils/api/Company';
import ChatApi from 'utils/api/Chats';
import DashboardApi from 'utils/api/Dashboard';
import InvoiceApi from 'utils/api/Invoices';
import LedgerApi from 'utils/api/Ledger';
import PortApi from 'utils/api/Ports';
import YardApi from 'utils/api/Yards';
import UserApi from 'utils/api/Users';
import UtilApi from 'utils/api/Util';
import VesselApi from 'utils/api/Vessels';
import VoyageApi from 'utils/api/Voyages';
import MailingApi from 'utils/api/Mailings';
import NotificationApi from 'utils/api/Notifications';
import PartyApi from 'utils/api/Parties';
import PermissionApi from 'utils/api/Permissions';
import PhotoApi from 'utils/api/Photos';
import TransportationApi from 'utils/api/Transportation';


declare module 'axios' {
    export interface AxiosRequestConfig {
        _silently?: boolean;
    }
}

export interface ApiBase {
    axios: AxiosInstance,
}

class Api implements ApiBase {
    readonly axios: AxiosInstance;

    private channels: {[name: string]: ((data: any) => void)[]} = {};

    auth: AuthApi;
    bl: BLApi;
    chats: ChatApi;
    company: CompanyApi;
    dashboard: DashboardApi;
    invoices: InvoiceApi;
    ledger: LedgerApi;
    ports: PortApi;
    yards: YardApi;
    vessels: VesselApi;
    voyages: VoyageApi;
    cars: CarApi;
    users: UserApi;
    util: UtilApi;
    mailings: MailingApi;
    notifications: NotificationApi;
    parties: PartyApi;
    permissions: PermissionApi;
    photos: PhotoApi;
    transportation: TransportationApi;

    constructor() {
        this.axios = axios.create({
            baseURL: process.env.REACT_APP_API_HOST,
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: true,
            withXSRFToken: true,
        });

        this.axios.interceptors.request.use(
            config => {
                this.publish('beforeCall', config);
                return config;
            },
            error => {
                return Promise.reject(error);
            }
        );

        this.axios.interceptors.response.use(
            response => {
                this.publish('afterCall', response);
                return response;
            },
            error => {
                this.publish('afterCall', error);
                this.publish(`onError${error.response?.status || 'Network'}`, error);

                return Promise.reject(error);
            }
        );

        this.auth = new AuthApi(this);
        this.bl = new BLApi(this);
        this.dashboard = new DashboardApi(this);
        this.invoices = new InvoiceApi(this);
        this.ledger = new LedgerApi(this);
        this.cars = new CarApi(this);
        this.chats = new ChatApi(this);
        this.company = new CompanyApi(this);
        this.ports = new PortApi(this);
        this.yards = new YardApi(this);
        this.vessels = new VesselApi(this);
        this.voyages = new VoyageApi(this);
        this.users = new UserApi(this);
        this.util = new UtilApi(this);
        this.mailings = new MailingApi(this);
        this.notifications = new NotificationApi(this);
        this.parties = new PartyApi(this);
        this.permissions = new PermissionApi(this);
        this.photos = new PhotoApi(this);
        this.transportation = new TransportationApi(this);
    }

    authorize = (token: string) => {
        this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    };

    subscribe = (name: string, listener: (data: AxiosRequestConfig | AxiosResponse) => void) => {
        if (!this.channels[name]) {
            this.channels[name] = []
        }

        this.channels[name].push(listener)
    };

    publish = (name: string, data: AxiosRequestConfig | AxiosResponse) => {
        this.channels[name]?.forEach(listener => listener(data))
    };
}

const api = new Api();

export default api;
